import type {
  LumpsumFundDistributionStatus,
  LumpsumImportantNote,
  LumpsumPlanRecommendationStatusReason,
  LumpsumPlanRecommendationTaxData,
  LumpsumPlanTransferType
} from './recommendation.types'
import type { LumpsumAllocationCategoryExecutionStatus } from '../../api/allocation.types'

export enum LumpsumAllocationImportantNoteStaticType {
  BUY_FUND_NON_RANK_ONE = 'BUY_FUND_NON_RANK_ONE',
  SWP_TRANSFER = 'SWP_TRANSFER',
  SELL_FUND_IDCW = 'SELL_FUND_IDCW',
  SELL_FUND_ELSS = 'SELL_FUND_ELSS',
  HIGH_EXPOSURE_SWITCH = 'HIGH_EXPOSURE_SWITCH',
  SELL_FUND_REGULAR = 'SELL_FUND_REGULAR',
  SELL_FUND_STCG = 'SELL_FUND_STCG'
}

export interface LumpsumAllocationImportantNoteFund {
  isin?: string | null
  fundName?: string | null
}

export interface LumpsumAllocationImportantNoteRuleBased {
  type?: LumpsumAllocationImportantNoteStaticType | string | null
  funds?: LumpsumAllocationImportantNoteFund[] | null
}

export interface LumpsumAllocationImportantNotes {
  ruleBased?: LumpsumAllocationImportantNoteRuleBased[] | null
  factBasedCuratedNotes?: LumpsumImportantNote[] | null
}

/** L2 sell accordion reason `reason` values. */
export enum LumpsumAllocationSellReasonType {
  MISSED_GAINS = 'MISSED_GAINS',
  POOR_PERFORMANCE = 'POOR_PERFORMANCE',
  INSIGNIFICANT_ALLOCATION = 'INSIGNIFICANT_ALLOCATION',
  TOO_MANY_FUNDS = 'TOO_MANY_FUNDS',
  CATEGORY_NOT_RECOMMENDED = 'CATEGORY_NOT_RECOMMENDED',
  HIGH_EXPOSURE = 'HIGH_EXPOSURE',
  CONCENTRATION_RISK = 'CONCENTRATION_RISK',
  REALLOCATED_PREFERENCE = 'REALLOCATED_PREFERENCE'
}

/** L2 buy accordion reason `reason` values. */
export enum LumpsumAllocationBuyReasonType {
  TOP_RANKED_FUND = 'TOP_RANKED_FUND',
  INCREASES_EXPOSURE = 'INCREASES_EXPOSURE',
  ADDS_EXPOSURE = 'ADDS_EXPOSURE',
  IMPROVES_DIVERSIFICATION = 'IMPROVES_DIVERSIFICATION',
  CONSOLIDATE_FUNDS = 'CONSOLIDATE_FUNDS',
  IMPROVES_PERFORMANCE = 'IMPROVES_PERFORMANCE'
}

export interface LumpsumAllocationSellReason {
  reason?: LumpsumAllocationSellReasonType | string | null
  missedGains?: number | null
  insignificantCategoryAllocationPercentage?: number | null
  currentCategoryFundCount?: number | null
  idealCategoryFundCount?: number | null
  idealCategoryCountInGroup?: number | null
  riskGroup?: string | null
  currentCategoryAllocationPercentage?: number | null
  idealCategoryAllocationPercentage?: number | null
  highExposureTargetPercentage?: number | null
}

export interface LumpsumAllocationBuyReason {
  reason?: LumpsumAllocationBuyReasonType | string | null
  currentCategoryAllocationPercentage?: number | null
  idealCategoryAllocationPercentage?: number | null
  currentCategoryFundCount?: number | null
  idealCategoryFundCount?: number | null
  xirrDiff?: number | null
  xirrDiffInMonths?: number | null
}

interface LumpsumAllocationPlanRecommendationFundDetailBase {
  isin: string | null
  fundName: string | null
  categoryName: string | null
  fundCategory: string | null
  derivedCategory?: string | null
  fundNature: string | null
  purchaseMode: string | null
  distributionStatus: LumpsumFundDistributionStatus | null
  elss: boolean | null
  stcg: boolean | null
  mfLogoUrl: string | null
  powerRating: string | null
  powerRank: number | null
  transferType: LumpsumPlanTransferType | null
  swpAmount: number | null
  swpDurationInMonths: number | null
  executed: boolean | null
  taxData: LumpsumPlanRecommendationTaxData | null
  categoryAllocationExecutionStatus?: LumpsumAllocationCategoryExecutionStatus | string | null
  categoryFundCountExecutionStatus?: LumpsumAllocationCategoryExecutionStatus | string | null
  sellReasons?: LumpsumAllocationSellReason[] | null
  buyReasons?: LumpsumAllocationBuyReason[] | null
}

export interface LumpsumAllocationSellFundDetail extends LumpsumAllocationPlanRecommendationFundDetailBase {
  actionType: string | null
  /** Per-leg sell ₹ for this switch pair. */
  sellAmount: number | null
  /** Per-leg buy ₹ for this switch pair. */
  buyAmount: number | null
}

export interface LumpsumAllocationBuyFundDetail extends LumpsumAllocationPlanRecommendationFundDetailBase {
  /** Per-leg buy ₹ for this switch pair. */
  buyAmount: number | null
  /** Per-leg sell ₹ for this switch pair. */
  sellAmount: number | null
}

export interface LumpsumAllocationSellPlanRecommendationDetails extends LumpsumPlanRecommendationStatusReason {
  isin: string | null
  fundName: string | null
  categoryName: string | null
  fundCategory: string | null
  fundNature: string | null
  purchaseMode: string | null
  distributionStatus: LumpsumFundDistributionStatus | null
  elss: boolean | null
  stcg: boolean | null
  taxCategory: string | null
  mfLogoUrl: string | null
  powerRating: string | null
  powerRank: number | null
  currentValue: number | null
  totalTransferAmount: number | null
  planSnapshotDate: number | null
  executed: boolean | null
  categoryAllocationExecutionStatus?: LumpsumAllocationCategoryExecutionStatus | string | null
  categoryFundCountExecutionStatus?: LumpsumAllocationCategoryExecutionStatus | string | null
  consolidatedTax?: LumpsumPlanRecommendationTaxData | null
  fundsToBeBought: LumpsumAllocationBuyFundDetail[] | null
  importantNotes?: LumpsumAllocationImportantNotes | null
}

export interface LumpsumAllocationBuyPlanRecommendationDetails extends LumpsumPlanRecommendationStatusReason {
  isin: string | null
  fundName: string | null
  categoryName: string | null
  fundCategory: string | null
  fundNature: string | null
  purchaseMode: string | null
  distributionStatus: LumpsumFundDistributionStatus | null
  elss: boolean | null
  stcg: boolean | null
  taxCategory: string | null
  mfLogoUrl: string | null
  powerRating: string | null
  powerRank: number | null
  currentValue: number | null
  totalTransferAmount: number | null
  planSnapshotDate: number | null
  executed: boolean | null
  categoryAllocationExecutionStatus?: LumpsumAllocationCategoryExecutionStatus | string | null
  categoryFundCountExecutionStatus?: LumpsumAllocationCategoryExecutionStatus | string | null
  consolidatedTax?: LumpsumPlanRecommendationTaxData | null
  fundsToBeSold: LumpsumAllocationSellFundDetail[] | null
  importantNotes?: LumpsumAllocationImportantNotes | null
}

export type LumpsumAllocationPlanRecommendationApiResponse =
  | LumpsumAllocationSellPlanRecommendationDetails
  | LumpsumAllocationBuyPlanRecommendationDetails
